// ============================================
// Hooks — Utilisateurs centralisés (Admin Hub)
// ============================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@gs/auth';
import { api } from '../services/api';

const QK = ['admin-users'] as const;

export type GsAppRole = 'ADMIN' | 'GESTIONNAIRE' | 'UTILISATEUR';

export interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  avatarInitials: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  appRoles: { appKey: string; role: string }[];
  servicePoints: string[];
}

export interface CreateAdminUserBody {
  email: string;
  fullName: string;
  appRoles: { appKey: string; role: GsAppRole }[];
  servicePoints: string[];
}

export function useAdminUsers() {
  const { user } = useAuth();
  return useQuery({
    queryKey: QK,
    enabled: !!user,
    queryFn: () => api.get<AdminUser[]>('/api/users'),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAdminUserBody) => api.post<AdminUser>('/api/users', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useUpdateUserAppRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, appKey, role }: { userId: string; appKey: string; role: GsAppRole | null }) =>
      api.patch<AdminUser>(`/api/users/${userId}/app-role`, { appKey, role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useUpdateUserServicePoints() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, servicePoints }: { userId: string; servicePoints: string[] }) =>
      api.patch<AdminUser>(`/api/users/${userId}/service-points`, { servicePoints }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useUpdateUserStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, isActive }: { userId: string; isActive: boolean }) =>
      api.patch<AdminUser>(`/api/users/${userId}/status`, { isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}
